import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Offcanvas, ListGroup, Button, Badge, Spinner } from 'react-bootstrap';
import { dateAsNumbersWithoutSeconds } from '../../../utils/dateFormatter';
import styles from './TrendRecordPanel.module.scss';

export default function TrendRecordPanel({
  show,
  records,
  highlightedRecordUid,
  chartType,
  userUid,
  onExclude,
  onClose,
  totalCount,
  sliceStart
}) {
  const { t } = useTranslation();
  const [confirmingUid, setConfirmingUid] = useState(null);
  const [excludingUid, setExcludingUid] = useState(null);

  const recordsAbove = sliceStart;
  const recordsBelow = totalCount - sliceStart - (records ? records.length : 0);

  const chartLabel = chartType ? t(`trends.${chartType}`) : '';
  const valueKey = chartType;

  const handleExclude = async (recordUid) => {
    setExcludingUid(recordUid);
    try {
      await onExclude(recordUid);
    } catch {
      // Error already handled by parent via toast
    } finally {
      setExcludingUid(null);
      setConfirmingUid(null);
    }
  };

  return (
    <Offcanvas show={show} onHide={onClose} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{chartLabel}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="p-0">
        {recordsAbove > 0 && (
          <div className={styles.boundaryIndicator}>
            ··· {recordsAbove} {t('trends.panel.earlier')}
          </div>
        )}

        <ListGroup variant="flush">
          {records && records.map((record) => {
            const isHighlighted = record.recordUid === highlightedRecordUid;
            const isConfirming = confirmingUid === record.recordUid;
            const isExcluding = excludingUid === record.recordUid;

            return (
              <ListGroup.Item
                key={record.recordUid}
                className={`${styles.recordRow} ${isHighlighted ? styles.highlighted : ''}`}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="fw-semibold">
                      {dateAsNumbersWithoutSeconds(record.recordDate)}
                    </div>
                    {record.description && (
                      <div className="text-muted small">{record.description}</div>
                    )}
                    {record.place && (
                      <div className="text-muted small">{record.place}</div>
                    )}
                  </div>
                  {valueKey && record[valueKey] != null && (
                    <Badge bg="light" text="dark" className="ms-2 align-self-center">
                      {record[valueKey]}
                    </Badge>
                  )}
                </div>

                <div className="mt-2 d-flex align-items-center gap-2">
                  {!isConfirming && (
                    <a
                      href={`/user/${userUid}/records/${record.recordUid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      {t('trends.panel.openRecord')}
                    </a>
                  )}

                  {!isConfirming ? (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setConfirmingUid(record.recordUid)}
                      disabled={isExcluding}
                    >
                      {t('trends.panel.exclude')}
                    </Button>
                  ) : (
                    <span className="d-inline-flex align-items-center gap-1">
                      <small className="text-danger">{t('trends.panel.confirmExclude')}</small>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleExclude(record.recordUid)}
                        disabled={isExcluding}
                      >
                        {isExcluding ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t('trends.panel.exclude')
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setConfirmingUid(null)}
                        disabled={isExcluding}
                      >
                        {t('trends.panel.cancel')}
                      </Button>
                    </span>
                  )}
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>

        {recordsBelow > 0 && (
          <div className={styles.boundaryIndicator}>
            ··· {recordsBelow} {t('trends.panel.more')}
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
